// 작품 만들기 — 제목과 AI 사용 정도만 있으면 된다. 만들면 바로 첫 회차 쓰기로 간다
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePluginMessages } from '../../../composition/plugin-runtime';
import { useCreateWork, type SerialApi } from '../composition/use-serial';
import { AI_USAGES, MAX_SYNOPSIS_CHARS, MAX_TITLE_CHARS, codePoints, type AiUsage } from '../domain/serial';
import { ErrorText, SignInGate } from './parts';
import { Input } from './whif/shared/ui/input';
import { Textarea } from './whif/shared/ui/textarea';
import { WhifButton } from './whif/shared/ui/whif-button';

export function newWorkPage(api: SerialApi) {
  return function SerialNewWorkPage() {
    const t = usePluginMessages('serial');
    const navigate = useNavigate();
    const create = useCreateWork(api);
    const [title, setTitle] = useState('');
    const [synopsis, setSynopsis] = useState('');
    // AI 사용 정도는 기본값 없이 작가가 직접 고른다 — 밝히는 것이 이 앱의 약속이다
    const [aiUsage, setAiUsage] = useState<AiUsage | null>(null);
    const titleChars = codePoints(title.trim());
    const ready = titleChars > 0 && titleChars <= MAX_TITLE_CHARS && codePoints(synopsis.trim()) <= MAX_SYNOPSIS_CHARS && aiUsage !== null;

    return (
      <SignInGate>
        <form
          className="flex flex-col gap-6 pt-6"
          onSubmit={e => {
            e.preventDefault();
            if (!ready) return;
            create.mutate({ title, synopsis, aiUsage }, { onSuccess: w => void navigate(`/works/${w.id}/write`) });
          }}
        >
          <h1 className="text-title-lg">{t('new.heading')}</h1>
          <Input label={t('new.title')} aria-label={t('new.title')} value={title} onChange={e => setTitle(e.target.value)} maxLength={MAX_TITLE_CHARS} />
          <Textarea
            label={t('new.synopsis')}
            aria-label={t('new.synopsis')}
            value={synopsis}
            onChange={e => setSynopsis(e.target.value)}
            maxLength={MAX_SYNOPSIS_CHARS}
            size="md"
          />
          <fieldset className="flex flex-col gap-2">
            <legend className="pb-2 text-sm font-semibold">{t('new.aiUsage')}</legend>
            {AI_USAGES.map(u => (
              <label
                key={u}
                className={`flex cursor-pointer items-start gap-3 rounded-global-radius-12 border p-4 transition-colors ${
                  aiUsage === u ? 'border-line-stroke-brand-primary-solid bg-bg-brand-primary-weak' : 'border-serial-line bg-serial-surface hover:bg-serial-surface-raised'
                }`}
              >
                <input type="radio" name="ai-usage" value={u} checked={aiUsage === u} onChange={() => setAiUsage(u)} className="mt-1 accent-[var(--bg-brand-primary-solid)]" />
                <span className="flex flex-col gap-1">
                  <span className="text-label-md-sb">{t(`ai.${u}`)}</span>
                  <span className="text-label-xs-m text-text-neutral-subtle">{t(`ai.${u}.hint`)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <ErrorText error={create.error} />
          <WhifButton type="submit" size="xlg" isFullWidth disabled={!ready || create.isPending}>
            {t('new.submit')}
          </WhifButton>
        </form>
      </SignInGate>
    );
  };
}
